import { useState, useEffect, useMemo } from 'react';
import { HeartIcon, FlagIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, PencilSquareIcon } from '@heroicons/react/24/solid';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Camp, CampFavorite } from '../types/database';
import { api } from '../lib/api';
import { track } from '../lib/track';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import CategoryTags from './ui/CategoryTags';
import { useTheme } from '../lib/theme/index.js';
import { getCategoryIcons } from '../lib/categoryPlaceholders';
import { getCampImage } from '../lib/images';

interface CampCardProps {
  camp: Camp & { camp_favorites?: CampFavorite[] };
  onFavoriteChange?: () => void;
  onEdit?: (camp: Camp) => void;
}

export default function CampCard({ camp, onFavoriteChange, onEdit }: CampCardProps) {
  const [heroImage, setHeroImage] = useState('');
  const [logoImage, setLogoImage] = useState('');
  const [isFavorite, setIsFavorite] = useState(Boolean(camp.camp_favorites && camp.camp_favorites.length > 0));
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();

  // Check for preview token in URL
  const searchParams = new URLSearchParams(location.search);
  const previewToken = searchParams.get('preview');

  // Check for admin preview images override (from localStorage) - per camp
  // Use state to avoid hydration mismatch
  const [adminPreviewImagesCamps, setAdminPreviewImagesCamps] = useState<Set<string>>(new Set());
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('admin_preview_images_camps');
      if (stored) {
        setAdminPreviewImagesCamps(new Set(JSON.parse(stored)));
      }
      
      // Listen for changes
      const handleStorageChange = () => {
        const updated = localStorage.getItem('admin_preview_images_camps');
        if (updated) {
          setAdminPreviewImagesCamps(new Set(JSON.parse(updated)));
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('adminPreviewImagesChanged', handleStorageChange);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('adminPreviewImagesChanged', handleStorageChange);
      };
    }
  }, []);

  // Determine if we should show images (camp is paying OR has valid preview token OR admin preview override)
  const shouldShowImages = useMemo(() => {
    // Admin preview override - shows all images to everyone regardless of payment status
    // Ensure camp.id is a string for consistent comparison
    const campIdString = String(camp.id);
    const adminPreviewImages = adminPreviewImagesCamps.has(campIdString);
    if (adminPreviewImages) {
      return true;
    }
    // Check if camp is paying (plan_id exists and is not null/empty)
    const isPaying = camp.plan_id !== null && camp.plan_id !== undefined && camp.plan_id !== '';
    // Check if preview token matches
    const hasValidPreviewToken = previewToken && camp.preview_token && previewToken === camp.preview_token;
    const result = isPaying || hasValidPreviewToken;
    
    // Debug logging for all camps in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[CampCard] Image visibility check:', {
        campName: camp.name,
        plan_id: camp.plan_id,
        plan_id_type: typeof camp.plan_id,
        isPaying,
        previewToken,
        campPreviewToken: camp.preview_token,
        hasValidPreviewToken,
        adminPreviewImages,
        shouldShowImages: result,
        heroImage: heroImage ? 'has image' : 'no image'
      });
    }
    
    return result;
  }, [camp?.plan_id, camp?.preview_token, camp?.name, camp?.id, previewToken, heroImage, adminPreviewImagesCamps]);

  useEffect(() => {
    // CRITICAL: Always check payment status first, before setting any images
    // Admin preview override - shows all images to everyone regardless of payment status
    // Ensure camp.id is a string for comparison
    const campIdString = String(camp.id);
    const adminOverride = adminPreviewImagesCamps.has(campIdString);
    
    // Calculate payment status and preview token first
    const planId = camp?.plan_id;
    const isPaying = !!(planId && planId !== null && planId !== undefined && planId !== '');
    const urlPreviewToken = previewToken;
    const campPreviewToken = camp?.preview_token;
    const hasValidPreviewToken = !!(urlPreviewToken && campPreviewToken && urlPreviewToken === campPreviewToken);
    const canShowImages = adminOverride || isPaying || hasValidPreviewToken;
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      if (adminPreviewImagesCamps.size > 0) {
        console.log('[CampCard] Admin preview check:', {
          campId: campIdString,
          campName: camp.name,
          adminPreviewImagesCamps: Array.from(adminPreviewImagesCamps),
          hasPreview: adminOverride,
          campImagesCount: camp.camp_images?.length || 0
        });
      }
      // Also log if camp has images but they're not showing
      if (camp.camp_images && camp.camp_images.length > 0 && !adminOverride && !isPaying) {
        console.log('[CampCard] Camp has images but preview not enabled:', {
          campId: campIdString,
          campName: camp.name,
          imagesCount: camp.camp_images.length,
          planId: camp.plan_id,
          displayImages: camp.display_images
        });
      }
    }
    
    // If we can't show images, clear them immediately
    if (!canShowImages) {
      setHeroImage('');
      setLogoImage('');
      return;
    }

    // Only set images if camp is paying OR has valid preview token OR admin override
    // Admin override: show all images regardless of display_images/display_logo flags
    if (adminOverride) {
      // Show all photos - get the first one for hero
      const photos = camp.camp_images?.filter(img => img.type === 'photo') || [];
      const logos = camp.camp_images?.filter(img => img.type === 'logo') || [];
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[CampCard] Admin override active - showing images:', {
          campName: camp.name,
          photosCount: photos.length,
          logosCount: logos.length
        });
      }
      
      if (photos.length > 0) {
        setHeroImage(photos[0].url);
      } else {
        setHeroImage('');
      }
      
      if (logos.length > 0) {
        setLogoImage(logos[0].url);
      } else {
        setLogoImage('');
      }
    } else {
      // Normal flow: Get the first photo for hero image (only if display_images is true)
      const photo = camp.camp_images?.find(img => img.type === 'photo');
      const logo = camp.camp_images?.find(img => img.type === 'logo');
      
      if (photo && camp.display_images) {
        setHeroImage(photo.url);
      } else {
        setHeroImage(''); // No placeholder image
      }
      
      if (logo && camp.display_logo) {
        setLogoImage(logo.url);
      } else {
        setLogoImage('');
      }
    }
  }, [camp, camp?.plan_id, camp?.preview_token, previewToken, shouldShowImages, adminPreviewImagesCamps]);

  // Check if user is admin
  useEffect(() => {
    if (session?.user?.id) {
      void supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        })
        .then(({ data }) => {
          setIsAdmin(!!data);
        })
        .catch((error: Error) => {
          console.error('Error checking admin status:', error);
        });
    }
  }, [session?.user?.id]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isUpdating) return;

    if (!session) {
      toast((t) => (
        <span className="flex items-center gap-2">
          <span className="whitespace-nowrap">Please sign in to save camps</span>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              navigate('/login');
            }}
            className="whitespace-nowrap btn btn-primary btn-sm"
          >
            Sign In
          </button>
        </span>
      ), {
        duration: 5000,
      });
      return;
    }

    try {
      setIsUpdating(true);
      const isNowFavorite = await api.camps.toggleFavorite(camp.id);
      setIsFavorite(isNowFavorite);
      if (isNowFavorite) {
        track('favorite_added', { camp_id: camp.id });
      } else {
        track('favorite_removed', { camp_id: camp.id, persona: 'parent' });
      }
      // Invalidate favorites query cache to ensure fresh data
      queryClient.invalidateQueries(['favorite-camps']);
      onFavoriteChange?.();
      toast.success(isNowFavorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(camp);
  };

  const handleViewDetails = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Camp data:', camp);
    
    if (!camp.slug) {
      console.log('Camp has no slug, generating one...');
      // Generate a slug from the name, city, and state
      const stateSlug = camp.state.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const citySlug = camp.city.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const nameSlug = camp.name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const baseSlug = `${stateSlug}/${citySlug}/${nameSlug}`;
      
      try {
        // Try to update with the generated slug
        let { data: updatedCamp, error: updateError } = await supabase
          .from('camps')
          .update({ slug: baseSlug })
          .eq('id', camp.id)
          .select()
          .single();
        
        if (updateError) {
          console.error('Error updating camp slug:', updateError);
          // If there's a unique constraint violation, try with a number suffix
          if (updateError.code === '23505') {
            let counter = 1;
            let newSlug = `${baseSlug}-${counter}`;
            let success = false;
            while (!success && counter < 100) {
              const { data: retryCamp, error: retryError } = await supabase
                .from('camps')
                .update({ slug: newSlug })
                .eq('id', camp.id)
                .select()
                .single();
              if (!retryError) {
                updatedCamp = retryCamp;
                success = true;
              } else {
                counter++;
                newSlug = `${baseSlug}-${counter}`;
              }
            }
            if (!success) {
              throw new Error('Failed to generate unique slug');
            }
          } else {
            throw updateError;
          }
        }
        
        if (!updatedCamp) {
          throw new Error('Failed to update camp slug');
        }
        
        console.log('Updated camp with slug:', updatedCamp);
        navigate(`/camps/${updatedCamp.slug}`);
        return;
      } catch (error) {
        console.error('Error generating slug:', error);
        toast.error('Failed to load camp details');
        return;
      }
    }
    
    // Check if the slug is in the old format (city/name) and update to new format (state/city/name)
    if (camp.slug && camp.slug.split('/').length === 2) {
      console.log('Camp has old slug format (city/name), updating to new format (state/city/name)...');
      const stateSlug = camp.state.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const citySlug = camp.city.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const nameSlug = camp.name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      const newSlug = `${stateSlug}/${citySlug}/${nameSlug}`;
      
      // Check if this would create a duplicate slug
      const { data: existingCamp } = await supabase
        .from('camps')
        .select('id')
        .eq('slug', newSlug)
        .neq('id', camp.id)
        .single();
      
      if (existingCamp) {
        console.log('Slug would be duplicate, using current slug');
        navigate(`/camps/${camp.slug}`);
        return;
      }
      
      try {
        const { data: updatedCamp, error: updateError } = await supabase
          .from('camps')
          .update({ slug: newSlug })
          .eq('id', camp.id)
          .select()
          .single();
        
        if (!updateError && updatedCamp) {
          console.log('Updated camp with new slug format:', updatedCamp);
          navigate(`/camps/${updatedCamp.slug}`);
          return;
        } else {
          console.error('Error updating camp slug:', updateError);
        }
      } catch (error) {
        console.error('Error updating slug format:', error);
      }
    }
    
    console.log('Navigating to:', `/camps/${camp.slug}`);
    navigate(`/camps/${camp.slug}`);
  };

  // Get category icons for placeholder
  const categoryIcons = getCategoryIcons(camp.camp_categories);
  
  // Create unique seed for this camp
  const campSeed = camp.id ? parseInt(camp.id.replace(/[^0-9]/g, '').slice(0, 8), 10) : 0;
  
  // Generate unique gradient for each card (3 clean purple-orange variants)
  const gradientVariants = [
    'linear-gradient(135deg, #C4B5FD 0%, #FED7AA 100%)', // Light purple to light orange
    'linear-gradient(135deg, #DDD6FE 0%, #FED7AA 100%)', // Very light purple to light orange (LIGHTER!)
    'linear-gradient(135deg, #C4B5FD 0%, #FDBA74 100%)', // Light purple to soft orange
  ];
  const gradientIndex = campSeed % gradientVariants.length;
  const selectedGradient = gradientVariants[gradientIndex];

  return (
    <div 
      onClick={handleViewDetails}
      className="camp-card min-h-[500px] bg-white rounded-lg shadow-md transition-all duration-300 hover:shadow-2xl cursor-pointer relative flex flex-col group overflow-visible"
    >
      {/* Header section with image/gradient - keeping overflow-hidden for rounded corners */}
      {/* Only show image if camp is paying OR has valid preview token */}
      <div className="h-32 relative overflow-hidden rounded-t-lg">
        {(() => {
          // Check admin preview override first (shows images to everyone)
          const campIdString = String(camp.id);
          const adminOverride = adminPreviewImagesCamps.has(campIdString);
          
          // Check plan_id - must be truthy (not null, undefined, or empty string)
          const planId = camp?.plan_id;
          const isPaying = !!(planId && planId !== null && planId !== undefined && planId !== '');
          
          // Check preview token
          const urlPreviewToken = previewToken;
          const campPreviewToken = camp?.preview_token;
          const hasValidPreviewToken = !!(urlPreviewToken && campPreviewToken && urlPreviewToken === campPreviewToken);
          
          // Admin override shows images to everyone, regardless of payment status
          const canShowImage = adminOverride || isPaying || hasValidPreviewToken;
          
          // Force clear heroImage if we can't show it
          if (!canShowImage && heroImage) {
            // This shouldn't happen due to useEffect, but as a safety net
            console.warn('[CampCard] Image should not be shown but heroImage is set:', {
              campName: camp?.name,
              plan_id: planId,
              isPaying,
              hasValidPreviewToken,
              heroImage
            });
          }
          
          // Only paying/preview/admin: show real photo or default if no photo yet. Others: gradient.
          const imageUrl = canShowImage ? (heroImage || getCampImage(camp)) : null;
          if (imageUrl) {
            return (
              <img
                src={imageUrl}
                alt={camp.name + " activities in " + camp.zip_code}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
            );
          }
          // Gradient placeholder for non-paying camps
          return (
          <div 
            className="w-full h-full relative overflow-hidden"
            style={{
              background: selectedGradient,
              backgroundSize: '110% 110%',
              backgroundPosition: 'center',
              transition: 'background-position 0.5s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundPosition = '60% 60%';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundPosition = 'center';
            }}
          >
            {categoryIcons.length > 0 && (
              <div className="absolute inset-0 pointer-events-none">
                {categoryIcons.map((iconConfig, index) => {
                  const positions = [
                    { bottom: '-1.5rem', right: '-1.5rem', size: 'w-32 h-32' },
                    { bottom: '1.5rem', right: '3rem', size: 'w-20 h-20' },
                    { bottom: '2.5rem', right: '-0.5rem', size: 'w-16 h-16' },
                    { bottom: '0.5rem', right: '5rem', size: 'w-24 h-24' },
                  ];
                  const position = positions[index % positions.length];
                  return (
                    <div
                      key={index}
                      className={`absolute ${position.size}`}
                      style={{
                        ...Object.fromEntries(
                          Object.entries(position).filter(([key]) => key !== 'size')
                        ),
                      }}
                    >
                      <div
                        className="w-full h-full flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                        style={{
                          opacity: index === 0 ? 0.12 : 0.08,
                          filter: 'blur(0.5px) drop-shadow(0 0 6px rgba(255, 255, 255, 0.15))',
                        }}
                      >
                        {iconConfig.icon}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle, rgba(139, 92, 246, 0.08) 1px, transparent 1px)`,
                backgroundSize: '5px 5px',
              }}
            />
          </div>
          );
        })()}
        {/* Only show logo if camp is paying OR has valid preview token */}
        {(() => {
          const isPaying = camp.plan_id !== null && camp.plan_id !== undefined && camp.plan_id !== '';
          const hasValidPreviewToken = previewToken && camp.preview_token && previewToken === camp.preview_token;
          const canShowImage = isPaying || hasValidPreviewToken;
          return canShowImage && logoImage ? (
            <div className="absolute top-4 left-4">
              <img
                src={logoImage}
                alt={camp.name + " logo"}
                className="w-16 h-16 rounded-lg shadow-lg object-cover bg-white"
              />
            </div>
          ) : null;
        })()}
      </div>
      
      {/* Category icons - positioned absolutely relative to card, outside overflow container */}
      {categoryIcons.length > 0 && (
        <div className="absolute top-[96px] left-4 flex gap-2 z-10">
          {categoryIcons.slice(0, 4).map((iconConfig, index) => (
            <div
              key={index}
              className="relative group/icon"
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="w-6 h-6 text-white opacity-90 hover:opacity-100 transition-opacity"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))',
                }}
              >
                {iconConfig.icon}
              </div>
              {/* Tooltip on hover - now can escape freely! */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/icon:opacity-100 transition-opacity pointer-events-none z-[9999]">
                {iconConfig.name}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="flex flex-col flex-grow p-6">
        <h3 className="text-2xl font-semibold text-gray-900 mb-3">{camp.name}</h3>
        <div className="text-gray-600 mb-4 line-clamp-2 flex-grow">
          {camp.description ? (
            <p className="text-sm leading-relaxed">
              {camp.description.split('\n\n')[0]?.substring(0, 120)}
              {camp.description.split('\n\n')[0]?.length > 120 ? '...' : ''}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">No description available</p>
          )}
        </div>
        <CategoryTags categories={camp.camp_categories || []} maxTags={4} className="mb-6" />
        <div className="mt-auto pt-4">
          <div className="text-sm text-gray-500 mb-4">
            <p className="mb-1">{camp.city}, {camp.state}</p>
            <p>Ages {camp.min_age}-{camp.max_age}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(e);
              }}
              className="flex-1 btn btn-primary"
            >
              View Details
            </button>
            {isAdmin && (
              <button
                onClick={handleEditClick}
                className="p-2 rounded-full bg-white hover:bg-purple-50 transition-colors"
                aria-label="Edit camp"
              >
                <PencilSquareIcon className="h-5 w-5 text-purple-600 fill-purple-600" />
              </button>
            )}
            {/* Action icons - next to View Details button */}
            <div className="flex items-center gap-2">
              {/* Flag icon - inactive */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.info('Report camp feature coming soon');
                }}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors opacity-60 cursor-not-allowed"
                aria-label="Report camp"
                title="Report camp (coming soon)"
                disabled
              >
                <FlagIcon className="h-4 w-4 text-gray-400" strokeWidth={1.5} />
              </button>
              {/* Favorite icon */}
              <button
                onClick={handleFavoriteClick}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isFavorite ? (
                  <HeartIconSolid className="h-4 w-4 text-primary fill-primary" />
                ) : (
                  <HeartIcon className="h-4 w-4 text-primary" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}